import { computed, ComputedRef, ref } from "vue";
import { storeToRefs } from "pinia";
import { DateTime } from "luxon";

import { useAuthStore } from "@/stores/Auth";
import { useUserStore } from "@/stores/User";
import { useWebinarsStore } from "@/stores/Webinars";

import { useCategoryFilter } from "@/composables/filters/category-filter";
import { useDateRangeFilter } from "@/composables/filters/date-range-filter";
import { useProductFilter } from "@/composables/filters/product-filter";
import { useTextFilter } from "@/composables/filters/text-filter";
import { useContentFilter } from "@/composables/filters/use-content-filter";

import { normalizeDateTime } from "@tmi/utils/date-time";

import type { Webinar } from "@tmi/types/entities";
import { webinarKey } from "@/types/products/product-definitions";

/*********************************************************************/
//                 GLOBALLY SHARED REACTIVE VARIABLES                //
/*********************************************************************/

export function useWebinarFilters() {
    const { isAuthenticated } = storeToRefs( useAuthStore() );
    const userStore = useUserStore();
    const webinarsStore = useWebinarsStore();
    const { allWebinars } = storeToRefs( webinarsStore );

    // -------------------------------- FILTER STATE --------------------------------- //
    const filterOutNonFavorites = ref< boolean >( false );

    // ------------------------------------ FILTERS ------------------------------------
    // The categories that have been selected to limit the results (inclusive)
    const { filterOutItemsThatDoNotContainSelectedCategories } = useCategoryFilter();

    // The time interval that captures the date range for which to return webinars
    const { isValidDateWithinDateRange } = useDateRangeFilter();

    // The products that have been selected to limit the results (inclusive)
    const { filterOutItemsThatDoNotContainSelectedProducts } = useProductFilter();

    // The search text entered by the user
    const { filterOutItemsThatDoNotContainSearchText } = useTextFilter();

    // Delegate the filtered/page/hasAny/loading scaffolding to the shared
    // primitive. The custom "N webinars found (X upcoming, Y past)" label is
    // built externally (below) rather than via the primitive's `buildLabel`
    // hook because upcomingWebinarsCount / pastWebinarsCount both derive
    // from `filteredWebinars` — passing them through `buildLabel` would
    // create a circular type dependency between the two computeds and the
    // primitive's return.
    const {
        filtered: filteredWebinars,
        filteredPage: filteredWebinarsPage,
        hasAnyResults: hasAnyWebinarResults,
        loading,
    } = useContentFilter< Webinar >( {
        itemNoun: 'webinar',
        exposeLoading: true,
        computeFiltered: () => {
            // Historical behavior: only shows results for authenticated users.
            if ( !isAuthenticated.value ) return [];

            const composedWebinars: Webinar[] = allWebinars.value.filter(
                ( webinar: Webinar ) => !filterOutNonFavorites.value || userStore.isFavorited( webinar.contentfulId, webinarKey )
            );

            return composedWebinars
                .filter( ( webinar: Webinar ): boolean => filterOutItemsThatDoNotContainSelectedCategories( webinar.categories ) )
                .filter( ( webinar: Webinar ): boolean => filterOutItemsThatDoNotContainSelectedProducts( webinar.products ) )
                .filter( ( webinar: Webinar ): boolean => isValidDateWithinDateRange( webinar.webinarDate ) )
                .filter( ( webinar: Webinar ): boolean => filterOutItemsThatDoNotContainSearchText( [ webinar.text, webinar.title ] ) )
                // Upcoming first, then most recent past webinars.
                .sort( ( left: Webinar, right: Webinar ) =>
                    normalizeDateTime( right.webinarDate ).toMillis() - normalizeDateTime( left.webinarDate ).toMillis()
                );
        },
    } );

    const upcomingWebinarsCount: ComputedRef< number > = computed( () =>
        filteredWebinars.value.filter( ( webinar: Webinar ) =>
            normalizeDateTime( webinar.webinarDate ).startOf( 'day' ) >= DateTime.local().startOf( 'day' )
        ).length
    );
    const pastWebinarsCount: ComputedRef< number > = computed( () => filteredWebinars.value.length - upcomingWebinarsCount.value );

    const webinarsSearchResultsLabel: ComputedRef< string > = computed( () => {
        const count = filteredWebinars.value.length;
        const noun = count > 1 ? 'webinars' : 'webinar';
        return `${ count } ${ noun } found (${ upcomingWebinarsCount.value } upcoming, ${ pastWebinarsCount.value } past)`;
    } );

    return {
        // T/F based on if filteredWebinars has any items
        hasAnyWebinarResults,
        // '#### results found'
        webinarsSearchResultsLabel,

        // A flag that defines if only the user's favorite webinars that match the given criteria
        // are returned or all that webinars that match the criteria
        filterOutNonFavorites,
        // The current single page sub-collection
        filteredWebinarsPage,
        // All webinars that match the current filter
        filteredWebinars,
        loading
    }
}

export type WebinarFilters = ReturnType< typeof useWebinarFilters >;
